import { useDownloadsNotification } from '@common/hooks';
import { useEffect } from 'react';
import { toast, type Id as ToastId } from 'react-toastify';
import { FormattedToastMessage } from '@components';
import { Box } from '@mui/material';
import {
  useActivitiesMetricsTodayQuery,
  useCarePlanDownloadMutation,
  useTableDownloadReportMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { DownloadsTableReportsType } from '@views/pages/Downloads/hooks';
import { useLocation } from 'react-router-dom';

let toastId: ToastId;
export const useHeader = () => {
  const { event } = useDownloadsNotification();
  const location = useLocation();

  const { mutateAsync: reportDownloadAsync, isPending: isReportDownloading } =
    useTableDownloadReportMutation();

  const {
    mutateAsync: carePlanDownloadAsync,
    isPending: isCarePlanDownloading,
  } = useCarePlanDownloadMutation();

  const {
    data = {
      activity_minutes_today: 0,
      care_note_minutes_today: 0,
    },
  } = useActivitiesMetricsTodayQuery();

  const handleReportDownload = async ({
    id,
    report_name,
    report_type,
    timestamp,
  }: {
    id: string;
    report_name: string;
    report_type: DownloadsTableReportsType;
    timestamp: string | null;
  }) => {
    if (isReportDownloading) return;

    try {
      await reportDownloadAsync({
        report_type,
        id,
        report_name,
        timestamp,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(toastId, { autoClose: 1 });
    }
  };

  const handleCarePlanDownload = async ({
    id,
    name,
    timestamp,
  }: {
    id: string;
    name: string;
    timestamp: string | null;
  }) => {
    if (isCarePlanDownloading) return;

    try {
      await carePlanDownloadAsync({
        id,
        name,
        timestamp,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(toastId, { autoClose: 1 });
    }
  };

  useEffect(() => {
    if (event?.payload && event.type === 'report-download.completed') {
      const { payload } = event;
      toastId = toast.success(
        <FormattedToastMessage
          title={'File is ready!'}
          message={'Find all files you requested to download in ‘Downloads’.'}
        />,
        {
          closeButton: (
            <Box
              onClick={() =>
                handleReportDownload({
                  id: payload?.report_download_id,
                  report_type: payload.report_type || 'patients_report',
                  report_name: payload.report_name || '',
                  timestamp: payload.generated_at || null,
                })
              }
              sx={{ width: '90px' }}
            >
              Download
            </Box>
          ),
          autoClose: false,
        },
      );
    }

    if (event?.payload && event.type === 'care-plan-download.completed') {
      const { payload } = event;

      toastId = toast.success(
        <FormattedToastMessage
          title={'File is ready!'}
          message={'Find all files you requested to download in ‘Downloads’.'}
        />,
        {
          closeButton: (
            <Box
              onClick={() =>
                handleCarePlanDownload({
                  id: payload?.care_plan_download_id || '',
                  name: payload.download_name || '',
                  timestamp: payload.generated_at || null,
                })
              }
              sx={{ width: '90px' }}
            >
              Download
            </Box>
          ),
          autoClose: false,
        },
      );
    }
  }, [event]);

  useEffect(() => {
    toast.dismiss();
  }, [location.pathname]);

  return {
    careNoteValue: data.care_note_minutes_today,
    activityLogValue: data.activity_minutes_today,
  };
};
